import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Filter, HelpCircle, Lightbulb, Search, ShieldCheck, TriangleAlert } from 'lucide-react';

import { Button } from './Button';
import { InboxListItem } from './InboxListItem';
import { InboxDetail } from './InboxDetail';
import { useInbox, type InboxItemKind, type InboxFilters } from '../hooks/use-inbox';

// ─── Tweaks: persisted to localStorage under `shiftagent.inbox.tweaks` ───
export interface InboxTweaks {
  density: 'comfy' | 'compact';
  layout: 'split' | 'list' | 'kanban';
  grouping: InboxFilters['grouping'];
  show_resolved: boolean;
  notification_style: 'loud' | 'quiet' | 'both';
}

const DEFAULT_TWEAKS: InboxTweaks = {
  density: 'comfy',
  layout: 'split',
  grouping: 'stage',
  show_resolved: true,
  notification_style: 'both',
};

function loadTweaks(): InboxTweaks {
  try {
    const raw = localStorage.getItem('shiftagent.inbox.tweaks');
    if (!raw) return DEFAULT_TWEAKS;
    return { ...DEFAULT_TWEAKS, ...(JSON.parse(raw) as Partial<InboxTweaks>) };
  } catch {
    return DEFAULT_TWEAKS;
  }
}

function saveTweaks(t: InboxTweaks) {
  try {
    localStorage.setItem('shiftagent.inbox.tweaks', JSON.stringify(t));
  } catch {
    /* ignore */
  }
}

export interface InboxViewProps {
  onOpenTask?: (taskId: string) => void;
  onOpenConversation?: (conversationId: string) => void;
}

const KIND_FILTERS: { kind: InboxItemKind; label: string; Icon: typeof ShieldCheck }[] = [
  { kind: 'approval', label: 'Approvals', Icon: ShieldCheck },
  { kind: 'review', label: 'Review', Icon: CheckCircle2 },
  { kind: 'question', label: 'Questions', Icon: HelpCircle },
  { kind: 'failure', label: 'Needs you', Icon: TriangleAlert },
  { kind: 'suggestion', label: 'Suggestions', Icon: Lightbulb },
];

export function InboxView({ onOpenTask, onOpenConversation }: InboxViewProps) {
  const [tweaks, setTweaks] = useState<InboxTweaks>(() => loadTweaks());
  const patch = (p: Partial<InboxTweaks>) => setTweaks((t) => ({ ...t, ...p }));

  useEffect(() => {
    saveTweaks(tweaks);
  }, [tweaks]);

  const {
    items,
    counts,
    selectedItem,
    selectedId,
    selectItem,
    resolveItem,
    archiveItem,
    filters,
    setFilters,
  } = useInbox();

  // Sync tweaks → filters
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      grouping: tweaks.grouping,
      show_resolved: tweaks.show_resolved,
    }));
  }, [tweaks.grouping, tweaks.show_resolved, setFilters]);

  const grouped = useMemo(() => {
    if (filters.grouping === 'none') return [{ label: 'All', items }];
    if (filters.grouping === 'priority') {
      const order: Array<'high' | 'med' | 'low'> = ['high', 'med', 'low'];
      return order
        .map((p) => ({
          label:
            p === 'high' ? 'High priority' : p === 'med' ? 'Normal' : 'Low priority',
          items: items.filter((i) => i.priority === p),
        }))
        .filter((g) => g.items.length > 0);
    }
    if (filters.grouping === 'agent') {
      const byAgent = new Map<string, typeof items>();
      for (const it of items) {
        const list = byAgent.get(it.agent.name) ?? [];
        list.push(it);
        byAgent.set(it.agent.name, list);
      }
      return Array.from(byAgent.entries()).map(([label, list]) => ({ label, items: list }));
    }
    // grouping === 'stage' (default): Needs you → Resolved
    const needsYou = items.filter((i) => !i.resolved_at);
    const resolved = items.filter((i) => !!i.resolved_at);
    const groups: { label: string; items: typeof items }[] = [];
    if (needsYou.length) groups.push({ label: 'Needs you', items: needsYou });
    if (resolved.length && tweaks.show_resolved)
      groups.push({ label: 'Resolved', items: resolved });
    return groups;
  }, [items, filters.grouping, tweaks.show_resolved]);

  const toggleKindFilter = (kind: InboxItemKind) => {
    setFilters((f) => ({
      ...f,
      kinds: f.kinds.includes(kind) ? f.kinds.filter((k) => k !== kind) : [...f.kinds, kind],
    }));
  };

  return (
    <div data-testid="inbox-view" data-screen-label="Inbox" className="h-full flex flex-col bg-sd-background">
      {/* Toolbar ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-sd-3 px-sd-5 py-sd-3 border-b border-sd-border/30">
        <div>
          <h1 className="text-sd-lg font-semibold text-sd-text">Inbox</h1>
          <div className="text-sd-xs text-sd-text-secondary">
            {counts.needsYou > 0 ? (
              <>
                <span className="text-sd-text font-medium">{counts.needsYou} need you</span> ·{' '}
                {counts.unread} unread · {counts.total} total
              </>
            ) : (
              <>All caught up · {counts.total} total</>
            )}
          </div>
        </div>

        <div className="flex-1" />

        {/* Search */}
        <label className="relative">
          <Search
            size={14}
            aria-hidden
            className="absolute left-sd-2 top-1/2 -translate-y-1/2 text-sd-text-secondary"
          />
          <input
            type="search"
            placeholder="Search inbox…"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            className="pl-sd-7 pr-sd-3 py-sd-1 text-sd-sm rounded-md border border-sd-border/40 bg-sd-surface-elevated/30 text-sd-text placeholder:text-sd-text-secondary focus:outline-none focus:ring-2 focus:ring-sd-primary"
            aria-label="Search inbox"
          />
        </label>

        {/* Density */}
        <div className="flex items-center rounded-md border border-sd-border/40 p-[2px] text-sd-xs">
          {(['comfy', 'compact'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => patch({ density: d })}
              aria-pressed={tweaks.density === d}
              className={[
                'px-sd-2 py-[2px] rounded capitalize focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:outline-none',
                tweaks.density === d
                  ? 'bg-sd-surface-elevated text-sd-text'
                  : 'text-sd-text-secondary hover:text-sd-text',
              ].join(' ')}
            >
              {d}
            </button>
          ))}
        </div>

        <Button
          variant="tertiary"
          size="sm"
          onClick={() => patch({ show_resolved: !tweaks.show_resolved })}
          aria-pressed={tweaks.show_resolved}
        >
          {tweaks.show_resolved ? 'Hide resolved' : 'Show resolved'}
        </Button>
      </div>

      {/* Kind filter chips ────────────────────────────────────────── */}
      <div className="flex items-center gap-sd-2 px-sd-5 py-sd-2 border-b border-sd-border/30 overflow-x-auto">
        <Filter size={12} aria-hidden className="text-sd-text-secondary" />
        {KIND_FILTERS.map(({ kind, label, Icon }) => {
          const on = filters.kinds.includes(kind);
          const n = counts.byKind[kind];
          return (
            <button
              key={kind}
              type="button"
              onClick={() => toggleKindFilter(kind)}
              aria-pressed={on}
              data-testid={`inbox-filter-${kind}`}
              className={[
                'inline-flex items-center gap-sd-1 px-sd-2 py-[3px] rounded-full border text-sd-xs transition-colors',
                on
                  ? 'border-sd-primary bg-sd-primary/10 text-sd-text'
                  : 'border-sd-border/40 text-sd-text-secondary hover:text-sd-text hover:border-sd-border',
              ].join(' ')}
            >
              <Icon size={11} aria-hidden /> {label}
              <span className="ml-sd-1 text-sd-text-secondary">{n}</span>
            </button>
          );
        })}

        <div className="flex-1" />

        <label className="text-sd-xs text-sd-text-secondary flex items-center gap-sd-1">
          Group by
          <select
            value={filters.grouping}
            onChange={(e) =>
              patch({ grouping: e.target.value as InboxFilters['grouping'] })
            }
            className="bg-transparent border border-sd-border/40 rounded px-sd-1 py-[1px] text-sd-text focus:outline-none focus:ring-2 focus:ring-sd-primary"
          >
            <option value="stage">Stage</option>
            <option value="priority">Priority</option>
            <option value="agent">Agent</option>
            <option value="none">None</option>
          </select>
        </label>
      </div>

      {/* Split pane ───────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 flex">
        {/* List (left) */}
        <div
          role="listbox"
          aria-label="Inbox items"
          data-testid="inbox-list"
          className="w-full max-w-[420px] border-r border-sd-border/30 overflow-y-auto"
        >
          {grouped.length === 0 && (
            <div className="p-sd-6 text-center text-sd-sm text-sd-text-secondary">
              Nothing matches your filters.
            </div>
          )}
          {grouped.map((group) => (
            <section key={group.label}>
              <header className="sticky top-0 z-10 px-sd-4 py-sd-2 bg-sd-background/95 backdrop-blur text-sd-xs uppercase tracking-wide text-sd-text-secondary border-b border-sd-border/30">
                {group.label} · {group.items.length}
              </header>
              {group.items.map((it) => (
                <InboxListItem
                  key={it.id}
                  item={it}
                  active={it.id === selectedId}
                  density={tweaks.density}
                  onSelect={selectItem}
                />
              ))}
            </section>
          ))}
        </div>

        {/* Detail (right) */}
        <div className="flex-1 min-w-0">
          <InboxDetail
            item={selectedItem}
            onResolve={resolveItem}
            onArchive={archiveItem}
            onOpenTask={onOpenTask}
            onOpenConversation={onOpenConversation}
          />
        </div>
      </div>
    </div>
  );
}
